/**
 * The module status summary on the pressure-control register (GET
 * /pressure-control/status, api#1281): certificates on a D036 extension, test
 * gauges whose calibration is not valid, and rig / stack drill warnings.
 *
 * The component counts by certificate status are NOT repeated here: the
 * register's filter chips already carry them. Every figure is the API's.
 * Drill warnings never block; they are listed, not enforced.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { PcBadge } from './PcSectionNav';
import { usePcStatus } from '../../hooks/usePressureControl';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { PC_CAL_FILL, PC_CAL_LABEL, PC_CALIBRATIONS, PC_DRILLS, pcDay, pcDueDaysPhrase } from '../../lib/pressure-control';
import { DhPanel } from '../../pages/downhole/DownholeBits';

export function PcModuleStatusPanel() {
  const query = usePcStatus();
  const failure = queryLoadFailure(query);
  const s = query.data?.status ?? null;
  const gauges = s?.gaugesNeedingCalibration ?? [];
  const warnings = s?.drills.warnings ?? [];
  const extended = s?.components.extended ?? 0;
  const clear = s !== null && gauges.length === 0 && warnings.length === 0 && extended === 0;

  return (
    <DhPanel eyebrow="Tests, drills and gauges" title="Needs attention" testId="pc-module-status">
      {query.isLoading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading status" />
        </div>
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the pressure-control summary" />
      ) : !s ? (
        <p className="px-4 py-3 text-sm text-fieldiq-text">—</p>
      ) : clear ? (
        <p className="px-4 py-3 text-sm text-fieldiq-text" data-testid="pc-status-clear">
          Nothing needs attention: no test gauge is out of calibration, no rig or stack is past its drill interval, and no certificate is on an extension.
        </p>
      ) : (
        <div className="divide-y divide-fieldiq-border">
          {extended > 0 && (
            <p className="px-4 py-3 text-sm text-fieldiq-text" data-testid="pc-status-extended">
              <span className="font-bold text-white">{extended}</span> {extended === 1 ? 'certificate is' : 'certificates are'} running on a D036 30-day extension; each component's page says which.
            </p>
          )}
          {gauges.length > 0 && (
            <div className="px-4 py-3" data-testid="pc-status-gauges">
              <p className="text-sm text-fieldiq-text">
                <span className="font-bold text-white">{gauges.length}</span> test {gauges.length === 1 ? 'gauge needs' : 'gauges need'} calibration. A test on it is refused.
              </p>
              <ul className="mt-2 space-y-1.5">
                {gauges.map((g) => (
                  <li key={g.equipmentId} className="flex flex-wrap items-center gap-2 text-sm">
                    <Link to={`${PC_CALIBRATIONS}/${g.equipmentId}`} className="font-semibold text-white underline-offset-2 hover:underline">{g.name ?? '—'}</Link>
                    <PcBadge value={g.status} labels={PC_CAL_LABEL} fills={PC_CAL_FILL} />
                    <span className="text-xs text-fieldiq-muted">
                      {g.dueOn ? `valid through ${pcDay(g.dueOn)} · ${pcDueDaysPhrase(g.daysUntilDue)}` : 'no calibration on record'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {warnings.length > 0 && (
            <div className="px-4 py-3" data-testid="pc-status-drills">
              <p className="text-sm text-fieldiq-text">
                Drill interval {s.drills.warnDays} days. A warning only; nothing is blocked.{' '}
                <Link to={PC_DRILLS} className="font-semibold underline underline-offset-2">Crew drills</Link>
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-fieldiq-text">
                {warnings.map((w, i) => <li key={`${w.id}-${i}`}>{w.message}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </DhPanel>
  );
}
